import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, Shield, Building2, Check } from 'lucide-react';

interface AgreementSection {
  num: string;
  title: string;
  items: string[];
}

interface AgreementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AGREEMENT_SECTIONS: AgreementSection[] = [
  {
    num: '01',
    title: 'Partes, alcance y definiciones',
    items: [
      'El presente acuerdo regula la relación entre PruneStone (en adelante, "el Proveedor") y la empresa o persona jurídica que complete el proceso de alta (en adelante, "la Empresa Usuaria").',
      'A efectos de este documento, "espacio de trabajo" refiere al entorno digital aislado asignado a la Empresa Usuaria dentro de la plataforma; "cuenta administradora" refiere al acceso con permisos totales creado durante el registro; y "usuario invitado" refiere a cualquier persona habilitada posteriormente por la cuenta administradora.',
      'Este acuerdo entra en vigencia en el momento en que la Empresa Usuaria confirma su aceptación durante el proceso de registro y permanece activo mientras la cuenta esté habilitada.',
    ],
  },
  {
    num: '02',
    title: 'Creación y gestión del espacio de trabajo',
    items: [
      'Al completar el registro, se genera un espacio de trabajo empresarial completamente independiente y lógicamente aislado de otras organizaciones dentro de la plataforma. Cada empresa opera en un entorno propio sin acceso a datos de terceros.',
      'El espacio de trabajo incluye acceso a los módulos habilitados: gestión de proyectos, unidades funcionales, clientes, proveedores, órdenes de compra, ventas, tesorería y reportes.',
      'La Empresa Usuaria es responsable de la configuración interna de su espacio de trabajo, incluyendo la creación de roles, asignación de permisos y gestión de usuarios invitados.',
    ],
  },
  {
    num: '03',
    title: 'Cuenta administradora: derechos y responsabilidades',
    items: [
      'La persona que complete el registro se convierte en administradora principal del espacio de trabajo, con permisos totales para modificar la configuración global, gestionar suscripciones y eliminar la organización.',
      'La Empresa Usuaria asume la responsabilidad de custodiar las credenciales de la cuenta administradora. En caso de pérdida o compromiso de acceso, deberá contactar al soporte técnico con la documentación correspondiente.',
      'La cuenta administradora no puede ser transferida a otra persona sin validación previa por parte del Proveedor, con el fin de garantizar la continuidad operativa y la seguridad del entorno.',
    ],
  },
  {
    num: '04',
    title: 'Invitación y gestión de usuarios',
    items: [
      'La habilitación de usuarios adicionales se realiza exclusivamente mediante el sistema de invitaciones por correo electrónico disponible dentro de la plataforma. No existe un proceso de registro abierto para empleados o colaboradores.',
      'Cada usuario invitado acepta los términos de uso al ingresar por primera vez. La Empresa Usuaria es responsable de que sus colaboradores conozcan y respeten las políticas de uso definidas en este acuerdo.',
      'La cuenta administradora puede suspender, modificar permisos o eliminar usuarios invitados en cualquier momento. El Proveedor no interviene en la gestión interna de usuarios de cada organización.',
    ],
  },
  {
    num: '05',
    title: 'Tratamiento de datos personales y empresariales',
    items: [
      'Los datos personales ingresados en la plataforma son recolectados y tratados conforme a la Ley 25.326 de Protección de Datos Personales de la República Argentina.',
      'La información empresarial cargada por la Empresa Usuaria es de su exclusiva titularidad. El Proveedor actúa como encargado del tratamiento y no cede, vende ni comparte estos datos con terceros, salvo obligación legal expresa.',
      'La Empresa Usuaria puede solicitar en cualquier momento el acceso, rectificación o eliminación de sus datos según los derechos que le otorga la normativa vigente, enviando la solicitud al canal de soporte oficial.',
    ],
  },
  {
    num: '06',
    title: 'Seguridad y confidencialidad de la información',
    items: [
      'El Proveedor implementa medidas técnicas y organizativas orientadas a garantizar la confidencialidad, integridad y disponibilidad de la información almacenada, incluyendo cifrado en tránsito y en reposo, control de acceso por roles y monitoreo de actividad.',
      'La Empresa Usuaria se compromete a no compartir credenciales de acceso entre usuarios, a utilizar contraseñas robustas y a notificar al Proveedor ante cualquier sospecha de acceso no autorizado.',
      'El Proveedor no se responsabiliza por brechas de seguridad derivadas del incumplimiento de las obligaciones de la Empresa Usuaria en materia de gestión de credenciales o uso negligente de la plataforma.',
    ],
  },
  {
    num: '07',
    title: 'Política de uso aceptable',
    items: [
      'La plataforma debe utilizarse exclusivamente para fines legítimos relacionados con la gestión operativa y administrativa de la empresa. Queda prohibido su uso para actividades ilícitas, fraudulentas o que violen derechos de terceros.',
      'Está expresamente prohibido intentar vulnerar los mecanismos de seguridad, acceder a datos de otras organizaciones, realizar ingeniería inversa del sistema o usar herramientas automatizadas para extracción masiva de datos.',
      'El incumplimiento de esta política habilitará al Proveedor a suspender o cancelar el acceso sin previo aviso, sin perjuicio de las acciones legales que correspondan.',
    ],
  },
  {
    num: '08',
    title: 'Disponibilidad, mantenimiento y soporte',
    items: [
      'El Proveedor se compromete a mantener la disponibilidad del servicio con el mayor nivel de continuidad posible, pero no garantiza disponibilidad ininterrumpida. Podrán realizarse mantenimientos programados con notificación previa.',
      'El soporte técnico se brinda a través de los canales oficiales habilitados. Los tiempos de respuesta varían según la criticidad del incidente y el tipo de cuenta.',
      'El Proveedor se reserva el derecho de actualizar la plataforma, incorporar nuevas funcionalidades o discontinuar módulos, comunicando los cambios con anticipación razonable.',
    ],
  },
  {
    num: '09',
    title: 'Limitación de responsabilidad',
    items: [
      'El Proveedor no será responsable por pérdidas de datos derivadas de errores del usuario, eliminaciones accidentales o uso indebido de las funciones de la plataforma. Se recomienda mantener respaldos periódicos de la información crítica.',
      'En ningún caso la responsabilidad total del Proveedor por daños directos o indirectos superará el importe abonado por la Empresa Usuaria en los últimos tres meses de servicio activo.',
      'El Proveedor no garantiza resultados comerciales específicos derivados del uso de la plataforma. La herramienta es un instrumento de gestión y su efectividad depende del uso correcto por parte de la organización.',
    ],
  },
  {
    num: '10',
    title: 'Vigencia, modificaciones y terminación',
    items: [
      'Este acuerdo tiene vigencia indefinida desde el momento de la aceptación. El uso continuado de la plataforma implica la aceptación tácita de cualquier modificación posterior, notificada con al menos 30 días de anticipación por correo electrónico.',
      'La Empresa Usuaria puede solicitar la cancelación de su cuenta en cualquier momento desde la configuración del sistema o contactando al soporte. Los datos serán retenidos por 30 días antes de su eliminación definitiva.',
      'El Proveedor puede dar de baja una cuenta por incumplimiento grave de este acuerdo, previa notificación y otorgando un plazo de subsanación cuando la naturaleza del incumplimiento lo permita.',
    ],
  },
];

const AGREEMENT_HIGHLIGHTS = [
  {
    icon: Building2,
    title: 'Espacio de trabajo',
    desc: 'Se crea un entorno empresarial propio e independiente para tu organización.',
    tone: 'indigo',
  },
  {
    icon: Shield,
    title: 'Privacidad de datos',
    desc: 'La información se trata con medidas de seguridad y bajo responsabilidad del proveedor.',
    tone: 'emerald',
  },
  {
    icon: Check,
    title: 'Uso responsable',
    desc: 'La cuenta administradora debe resguardar credenciales y controlar accesos internos.',
    tone: 'blue',
  },
] as const;

function AgreementModal({ open, onOpenChange }: AgreementModalProps) {
  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label="Cerrar acuerdo"
        className="absolute inset-0 cursor-default bg-slate-950/70"
        onClick={() => onOpenChange(false)}
      />

      <div className="relative z-10 flex max-h-[min(88vh,860px)] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-border bg-background shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-border bg-card px-5 py-4 sm:px-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              <FileText className="h-4 w-4 text-indigo-500" aria-hidden="true" />
              Acuerdo de servicio
            </div>
            <h2 className="text-xl font-bold text-foreground sm:text-2xl">
              Acuerdo de Servicio Empresarial — PruneStone
            </h2>
            <p className="text-sm text-muted-foreground">
              Versión 1.0 · Vigente desde enero de 2025 · Aplicable a todas las cuentas empresariales
            </p>
          </div>

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
          <div className="grid gap-3 lg:grid-cols-3">
            {AGREEMENT_HIGHLIGHTS.map(({ icon: Icon, title, desc, tone }) => (
              <div key={title} className="rounded-2xl border border-border bg-card p-4 shadow-sm">
                <div
                  className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl ${
                    tone === 'indigo'
                      ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                      : tone === 'emerald'
                        ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </div>
                <h3 className="text-sm font-semibold text-foreground">{title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 space-y-3">
            {AGREEMENT_SECTIONS.map(({ num, title, items }) => (
              <section key={num} className="rounded-2xl border border-border bg-card/70 p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-bold text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                    {num}
                  </span>
                  <h4 className="text-sm font-semibold leading-snug text-foreground sm:text-[15px]">{title}</h4>
                </div>

                <ul className="mt-3 space-y-2 pl-11">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm leading-relaxed text-muted-foreground">
                      <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-muted-foreground/40" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <p className="py-5 text-center text-xs text-muted-foreground/70">
            Acuerdo de Servicio Empresarial PruneStone · Versión 1.0 · enero 2025
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-border bg-card px-5 py-4 sm:px-6">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center justify-center rounded-xl border border-border px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default AgreementModal;